import { useMutation, useQuery, useQueryClient } from "react-query";

import { api } from "../api";
import { useAuth } from "../../auth/useAuth";
import {
  AuthorizeRequestInfo,
  ConnectedApp,
  ConsentData,
  OrganisationConnectedApp,
} from "./types";

export enum OAuthConnectionQueryKeys {
  AuthorizeRequest = "oauthAuthorizeRequest",
  ConsentData = "oauthConsentData",
  ConnectedApps = "oauthConnectedApps",
  OrganisationConnectedApps = "oauthOrganisationConnectedApps",
}

export const useAuthorizeRequest = (requestId: string | null) => {
  // The signed-in user's id scopes the cache key so an in-SPA account switch
  // (sign out → sign in as someone else on the same URL) never renders the
  // previous user's cached authorize-request. react-query 3 renders a key's
  // cached data before any refetch resolves, so a shared key would flash the
  // prior user's request during the refetch overrides below — the per-user key
  // prevents that.
  const { user } = useAuth();
  const userId = user?.id;
  return useQuery<AuthorizeRequestInfo, Error>(
    [OAuthConnectionQueryKeys.AuthorizeRequest, userId, requestId],
    () => api.get<AuthorizeRequestInfo>(`oauth/authorize-request/${requestId}`),
    {
      enabled: Boolean(requestId) && Boolean(userId),
      retry: false,
      // One-time requests expire server-side (10-min TTL) and org membership can
      // change in other tabs, so a refocus/remount must surface the current
      // truth rather than a cached snapshot. The consent page renders retained
      // data during background refetches (its !data gates), so this adds
      // freshness without flicker; a 404 on refocus correctly lands the
      // terminal card.
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );
};

export const useConsentData = (enabled: boolean) => {
  // See useAuthorizeRequest — the user id scopes the cache so org names/roles/
  // property counts can't leak across an in-place account switch.
  const { user } = useAuth();
  const userId = user?.id;
  return useQuery<ConsentData, Error>(
    [OAuthConnectionQueryKeys.ConsentData, userId],
    () => api.get<ConsentData>("oauth/consent-data"),
    {
      enabled: enabled && Boolean(userId),
      retry: false,
      // Mirror useAuthorizeRequest's freshness: the one-time request expires
      // server-side (10-min TTL) and org membership can change in another tab,
      // so a refocus/remount must surface the current org list, not a cached
      // snapshot. Retained data renders during the background refetch (the
      // page's !data gates), so no flicker.
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );
};

export const useApproveAuthorizeRequest = (requestId: string) =>
  useMutation<{ redirectUrl: string }, Error, string[]>((organisationIds) =>
    api.post<{ redirectUrl: string }>(`oauth/authorize-request/${requestId}/approve`, { organisationIds })
  );

export const useDenyAuthorizeRequest = (requestId: string) =>
  useMutation<{ redirectUrl: string }, Error, void>(() =>
    api.post<{ redirectUrl: string }>(`oauth/authorize-request/${requestId}/deny`)
  );

export const useConnectedApps = () => {
  // See useAuthorizeRequest — the user id scopes the cache so the connected-app
  // list can't leak across an in-place account switch. Gate on identity too.
  const { user } = useAuth();
  const userId = user?.id;
  return useQuery<ConnectedApp[], Error>(
    [OAuthConnectionQueryKeys.ConnectedApps, userId],
    () => api.get<ConnectedApp[]>("oauth/grants"),
    {
      enabled: Boolean(userId),
      // This is a security-audit surface: a grant added/revoked in another
      // tab or session must show up on revisit/refocus, so override the
      // app-wide refetchOnMount/refetchOnWindowFocus:false defaults here.
      // Cached data still renders instantly while the refetch runs.
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );
};

export const useRevokeConnectedApp = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, string>((grantId) => api.del(`oauth/grants/${grantId}`), {
    // Invalidate on settle (success AND error): a revoke that errors after the
    // server already processed it (lost response, concurrent revoke → 404) must
    // still reconcile the list, or the app stays listed as connected for the
    // rest of the session (all global refetch flags are off).
    // Returning the promise keeps the mutation pending (isLoading) until the
    // reconciliation refetch settles, so the modal's guards span the whole
    // decide-from-fresh-data window.
    onSettled: () => queryClient.invalidateQueries(OAuthConnectionQueryKeys.ConnectedApps),
  });
};

export const useOrganisationConnectedApps = (organisationId?: string) =>
  // Keyed on the organisation id so an in-SPA org switch can never render the
  // previous org's connected-app list before its own refetch resolves (mirrors
  // the per-user keying on useConnectedApps). The org itself is derived
  // server-side from the JWT scope, so the id is a cache key only — the request
  // carries no org param.
  useQuery<OrganisationConnectedApp[], Error>(
    [OAuthConnectionQueryKeys.OrganisationConnectedApps, organisationId],
    () => api.get<OrganisationConnectedApp[]>("oauth/organisation-grants"),
    {
      enabled: Boolean(organisationId),
      // Same audit-freshness overrides as useConnectedApps: a grant added or an
      // org-scoped revoke performed in another tab/session must surface on
      // revisit/refocus, so override the app-wide refetch:false defaults.
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );

export const useRevokeOrganisationConnectedApp = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, string>(
    (grantId) => api.del(`oauth/organisation-grants/${grantId}`),
    {
      // Invalidate on settle (success AND error) so a revoke whose response was
      // lost still reconciles the list. Invalidate BOTH keys: the same grant can
      // also appear in the authorizing user's own Connected Apps (User Settings),
      // so the user view must refresh too. Return the combined promise so the
      // mutation stays pending until both reconciliation refetches settle
      // (matching the single-key useRevokeConnectedApp).
      onSettled: () =>
        Promise.all([
          queryClient.invalidateQueries(OAuthConnectionQueryKeys.OrganisationConnectedApps),
          queryClient.invalidateQueries(OAuthConnectionQueryKeys.ConnectedApps),
        ]),
    }
  );
};
